import type { ReactNode } from 'react';
import { cx } from './cx';
import type { Tone } from './Badge';

export interface CardProps {
  title?: ReactNode;
  /** Small line under the title. Counts, dates, source of the figures. */
  subtitle?: ReactNode;
  /**
   * A Badge beside the title: the card's state in a word ("1 OVERDUE",
   * "2 UNOWNED", "PAID"). This is where a card's tone goes.
   */
  stamp?: ReactNode;
  /** Right-hand side of the header: filters, a link, one small button. */
  actions?: ReactNode;
  footer?: ReactNode;
  /**
   * @deprecated Draws nothing. It was a 2px coloured top rule, which the
   * direction bans by name ("no accent stripes on cards"); say the state
   * with `stamp` instead. Still accepted so a page not yet changed renders.
   */
  accent?: Tone;
  /** Remove body padding when the card holds a register that must reach the edge. */
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
  children?: ReactNode;
}

/**
 * A card is a white panel on the bone ground: a hairline, a 9px radius and the
 * quiet seated shadow. The ground-to-surface step does most of the separating;
 * the shadow only finishes it. No coloured stripe on any edge: a card that
 * needs to say it is late says so with a stamp, in words, which also
 * survives a black-and-white board pack. Most lists should not be cards at
 * all but register rows; a card is for a panel that needs a title and its
 * own edge.
 */
export function Card({
  title,
  subtitle,
  stamp,
  actions,
  footer,
  flush,
  className,
  bodyClassName,
  children,
}: CardProps) {
  return (
    <section className={cx('relative rounded-ct-md border border-line bg-paper-card text-ink shadow-card', className)}>
      {title || actions || stamp ? (
        <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-3">
          <div className="min-w-0">
            {title || stamp ? (
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                {title ? (
                  <h2 className="font-display text-lg font-semibold leading-tight tracking-[-0.015em] text-ink">
                    {title}
                  </h2>
                ) : null}
                {stamp}
              </div>
            ) : null}
            {subtitle ? <p className="mt-0.5 text-xs text-ink-3">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cx(flush ? '' : 'p-4', bodyClassName)}>{children}</div>
      {footer ? (
        <footer className="border-t border-line px-4 py-3 text-xs text-ink-2">{footer}</footer>
      ) : null}
    </section>
  );
}
